import { PrismicRichText } from '@prismicio/react';
import NextImage from 'next/image';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { SectionWrapper } from 'remote-com-shared/components/SectionWrapper';
import {
  fieldHasContent,
  groupFieldHasContent,
  transformProperties,
} from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { DescriptionComponent, TitleComponent as Title } from '../sharedStyles';

import { defaultPrismicComponents } from '@/src/domains/prismic/render';
import { tabletBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';

const IconWrapper = styled.div`
  text-align: ${({ $textAlign = 'left' }) => $textAlign};
  margin-bottom: 24px;
`;

const Image = styled(NextImage)`
  position: relative;
  z-index: 1;
`;

const TitleComponent = styled(Title)`
  ${fromTabletBreakpoint(css`
    ${fluidProp('margin-bottom', '54px', '120px')};
  `)}
`;

const EntryRow = styled.div`
  ${fluidProp(['padding-top', 'padding-bottom'], '24px', '48px')}
  font-family: var(--typography-fontBrand);
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  justify-items: center;
  column-gap: 64px;
  position: relative;

  ${tabletBreakpoint(css`
    grid-template-columns: 1fr;
    justify-items: start;
  `)}
`;

const EntryContent = styled.div`
  padding: 32px;
  position: relative;

  ${whenProp(
    '$odd',
    css`
      order: 1;
      ${tabletBreakpoint(css`
        order: 0;
      `)}
    `
  )}
`;

const EntryTitle = styled.div`
  font-family: var(--typography-fontBossa);
  color: var(--color-tone-primary);
  font-weight: 500;
  font-size: 32px;
  line-height: 1.45;

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: 16px 0;
  }
`;

const EntryDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  position: relative;
  z-index: 1;
  color: var(--color-tone-font);
  font-size: 18px;
`;

const customListComponent = {
  ...defaultPrismicComponents,
  listItem: ({ children }) => <ListItem>{children}</ListItem>,
};

const ListItem = styled.li`
  list-style: none;
  margin-bottom: 16px;
  padding-left: 8px;

  ::before {
    content: url(/images/icons/square-check.svg);
    font-size: 0.8em;
    position: absolute;
    left: 0px;
  }
`;

const CtaWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const CtaButton = styled(Button)`
  && {
    border-color: var(--color-tone-secondary);
    background: var(--color-tone-primary);
    margin: 0 auto;
  }
`;

export function AlternatingContentImageSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);

  const { sectionId, title, description, ctaLabel, ctaLink } = primary;

  return (
    <SectionWrapper id={sectionId}>
      <ContentContainer>
        {fieldHasContent(title) && (
          <TitleComponent>
            <PrismicRichText field={title} />
          </TitleComponent>
        )}
        {fieldHasContent(description) && (
          <DescriptionComponent>
            <PrismicRichText field={description} components={defaultPrismicComponents} />
          </DescriptionComponent>
        )}
        {groupFieldHasContent(fields, 'title') &&
          fields.map(({ icon, title: entryTitle, description: entryDescription, image }, index) => (
            <EntryRow key={index}>
              <EntryContent $odd={index % 2}>
                {icon && (
                  <IconWrapper>
                    <Image src={icon.url} {...icon.dimensions} />
                  </IconWrapper>
                )}
                {fieldHasContent(entryTitle) && (
                  <EntryTitle>
                    <PrismicRichText field={entryTitle} />
                  </EntryTitle>
                )}
                {fieldHasContent(entryDescription) && (
                  <EntryDescription>
                    <PrismicRichText field={entryDescription} components={customListComponent} />
                  </EntryDescription>
                )}
              </EntryContent>
              {image?.url && <Image src={image.url} {...image.dimensions} />}
            </EntryRow>
          ))}
        {ctaLink && (
          <CtaWrapper>
            <CtaButton href={ctaLink?.url} target={ctaLink?.target}>
              {ctaLabel}
            </CtaButton>
          </CtaWrapper>
        )}
      </ContentContainer>
    </SectionWrapper>
  );
}
